// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
    // Try to get from localStorage first (fallback)
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchUserLogs(page = 1, limit = 8, filters = {}) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });

    // Add filter parameters if they exist
    if (filters.date) {
        queryParams.append('date', filters.date);
    }
    if (filters.adminName && typeof filters.adminName === 'object' && filters.adminName._id) {
        queryParams.append('username', filters.adminName._id);
    }

    const url = `${API_BASE_URL}api/v1/dashboard/log/user?${queryParams.toString()}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }
    return data;
}

export async function fetchAdminLogs(page = 1, limit = 8, filters = {}) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });

    // Add filter parameters if they exist
    if (filters.date) {
        queryParams.append('date', filters.date);
    }
    if (filters.adminName && typeof filters.adminName === 'object' && filters.adminName._id) {
        queryParams.append('username', filters.adminName._id);
    }

    const url = `${API_BASE_URL}api/v1/dashboard/log/admin?${queryParams.toString()}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }
    return data;
}

export async function searchAdmins(email) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making search admins request to:', `${API_BASE_URL}api/v1/dashboard/log/admin-search`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/log/admin-search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ "email": email })
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to search admins';

        // Clone the response before reading it
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            // If response is not JSON, try reading as text from the cloned response
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}
